import { useState, useEffect, useCallback, useRef } from "react";

interface SpeedTestConfig {
  minDownloadMbps: number;
  minUploadMbps: number;
}

interface SpeedTestResult {
  downloadMbps: number;
  uploadMbps: number;
  isAboveThreshold: boolean;
}

interface UseSpeedTestReturn {
  isTesting: boolean;
  testResult: SpeedTestResult | null;
  showWarning: boolean;
  retryTest: () => void;
  dismissWarning: () => void;
  error: string | null;
  currentRetryCount: number;
  maxRetries: number;
  hasReachedMaxRetries: boolean;
}

const DEFAULT_CONFIG: SpeedTestConfig = {
  minDownloadMbps: 3,
  minUploadMbps: 1,
};

const measureDownloadSpeed = async (): Promise<number> => {
  const attempts: number[] = [];
  const targetDurationMs = 3000;
  const testStart = performance.now();

  while (performance.now() - testStart < targetDurationMs) {
    const bytes = 5 * 1024 * 1024;
    const url = `https://speed.cloudflare.com/__down?bytes=${bytes}`;
    const start = performance.now();
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error("Download test failed");
    await res.blob();
    const sec = (performance.now() - start) / 1000;
    const bits = bytes * 8;
    attempts.push(bits / sec / 1_000_000);
  }

  return attempts.reduce((sum, val) => sum + val, 0) / attempts.length;
};

const measureUploadSpeed = async (): Promise<number> => {
  const attempts: number[] = [];
  const targetDurationMs = 3000;
  const testStart = performance.now();

  while (performance.now() - testStart < targetDurationMs) {
    const bytes = 3 * 1024 * 1024;
    const url = "https://speed.cloudflare.com/__up";
    const payload = new Uint8Array(bytes);
    const start = performance.now();
    const res = await fetch(url, { method: "POST", body: payload, cache: "no-store" });
    if (!res.ok) throw new Error("Upload test failed");
    await res.text();
    const sec = (performance.now() - start) / 1000;
    const bits = bytes * 8;
    attempts.push(bits / sec / 1_000_000);
  }

  return attempts.reduce((sum, val) => sum + val, 0) / attempts.length;
};

export const useSpeedTest = (config: SpeedTestConfig = DEFAULT_CONFIG): UseSpeedTestReturn => {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<SpeedTestResult | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasRunInitialTest = useRef(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;

  const runSpeedTest = useCallback(
    async (isRetry = false) => {
      if (isRetry) {
        retryCountRef.current += 1;
      }

      setIsTesting(true);
      setError(null);

      try {
        const downloadMbps = await measureDownloadSpeed();
        const uploadMbps = await measureUploadSpeed();

        const isAboveThreshold =
          downloadMbps >= config.minDownloadMbps && uploadMbps >= config.minUploadMbps;

        setTestResult({
          downloadMbps,
          uploadMbps,
          isAboveThreshold,
        });
        setIsTesting(false);

        if (isAboveThreshold) {
          setTimeout(() => setShowWarning(false), 3000);
        } else {
          setShowWarning(true);
        }
      } catch (err) {
        setError("Test failed. Please check your connection.");
        setIsTesting(false);
        setShowWarning(true);
      }
    },
    [config.minDownloadMbps, config.minUploadMbps]
  );

  const retryTest = useCallback(() => {
    setTestResult(null);
    void runSpeedTest(true);
  }, [runSpeedTest]);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  useEffect(() => {
    if (!hasRunInitialTest.current) {
      hasRunInitialTest.current = true;
      void runSpeedTest();
    }
  }, [runSpeedTest]);

  return {
    isTesting,
    testResult,
    showWarning,
    retryTest,
    dismissWarning,
    error,
    currentRetryCount: retryCountRef.current,
    maxRetries: MAX_RETRIES,
    hasReachedMaxRetries: retryCountRef.current >= MAX_RETRIES,
  };
};
